import { useEffect, useRef, useState } from "react";

let lottieLoaderPromise = null;
const loadLottie = () => {
  if (lottieLoaderPromise) return lottieLoaderPromise;
  lottieLoaderPromise = import("lottie-web/build/player/lottie_light").then(mod => mod.default || mod);
  return lottieLoaderPromise;
};

/**
 * LottieToggle – a generic Lottie-powered toggle button.
 *
 * Props
 * ─────
 * animationData / src   – Lottie JSON object or URL path (one required)
 * isOn                  – controlled boolean state
 * toggleFunc            – called when the user clicks; you update isOn externally
 * width / height        – dimensions (px)
 *
 * onSegment             – { from, to, rest? } frame range played when transitioning TO "on"
 * offSegment            – { from, to, rest? } frame range played when transitioning TO "off"
 *
 * Each segment's `rest` frame is where the animation hard-snaps after the transition completes.
 * If omitted, `to` is used. Use `rest` when `to` is at a boundary or mid-squish frame.
 *
 * Default segments are calibrated for ToggleLightDark.json (216 frames @ 24 fps):
 *   Animation timeline:
 *     0–90    : light mode settled  (knob at RIGHT, white/purple pill)
 *     86→108  : light→dark transition (knob slides LEFT)
 *     108–195 : dark mode settled   (knob at LEFT, dark pill)
 *     195→216 : dark→light transition (knob slides RIGHT + squish until fr 216)
 *
 *   • onSegment  = { from: 86,  to: 108, rest: 108 } – transition TO dark  (isOn = true)
 *   • offSegment = { from: 195, to: 210, rest: 45  } – transition TO light (isOn = false)
 *     to=210: first frame where the knob is back at the right and colours are light.
 *     Lottie is paused immediately inside the complete callback (before its RAF can draw
 *     another tick), then snapped to rest=45 which is the same frame shown on initial load.
 *
 * transitionMs – target wall-clock duration for the animated transition (default 500 ms)
 */
const LottieToggle = ({
  animationData,
  src,
  isOn = false,
  toggleFunc,
  width = 40,
  height = 40,
  // eslint-disable-next-line no-unused-vars
  playOnHover = false,
  onSegment  = { from: 86,  to: 108, rest: 108 },
  offSegment = { from: 195, to: 210, rest: 45  },
  transitionMs = 500,
}) => {
  const containerRef    = useRef(null);
  const animationRef    = useRef(null);
  const animatingRef    = useRef(false);
  const snapTimerRef    = useRef(null);  // fallback setTimeout handle
  const rafHandle1Ref   = useRef(null);  // first rAF handle for post-complete snap
  const rafHandle2Ref   = useRef(null);  // second rAF handle for post-complete snap
  const completeHandler = useRef(null);  // current complete listener so we can remove it
  const lastSnappedRef  = useRef(null);  // last isOn value rendered to lottie (bool or null)
  const queuedTargetRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  /** Play [fromFrame, toFrame] forward, speed-scaled to fit desiredMs. */
  const playSegmentRange = (fromFrame, toFrame, desiredMs = 500) => {
    const anim = animationRef.current;
    if (!anim) return;
    try {
      const fr = anim.animationData?.fr ?? 24;
      const frames = Math.abs(toFrame - fromFrame);
      const segmentSec = frames / fr || 0.5;
      const speed = segmentSec / (desiredMs / 1000);
      try { anim.setSpeed(speed); } catch (e) {}
      try { anim.setDirection(1); } catch (e) {}
      const lo = Math.min(fromFrame, toFrame);
      const hi = Math.max(fromFrame, toFrame);
      anim.playSegments([lo, hi], true);
    } catch (e) {
      console.warn('LottieToggle playSegmentRange error', e);
    }
  };

  /** Hard-snap to the resting frame for the given on/off state. */
  const snapToSettledFrame = (anim, on) => {
    try {
      const seg = on ? onSegment : offSegment;
      const restFrame = seg.rest !== undefined ? seg.rest : seg.to;
      // After playSegments([lo, hi]), lottie sets firstFrame = lo internally.
      // goToAndStop(n, true) sets currentRawFrame = n, and the *rendered* frame
      // is currentRawFrame + firstFrame.  If firstFrame is e.g. 195 and we snap
      // to rest=45, lottie renders frame 240 (past op=216) → completely invisible.
      // resetSegments(true) resets firstFrame to 0 (ip) before we snap.
      anim.resetSegments(true);
      anim.goToAndStop(restFrame, true);
      lastSnappedRef.current = on;
    } catch (e) {
      // ignore
    }
  };

  /** Cancel all pending snap work (rAFs + fallback timeout + complete listener). */
  const cancelPendingSnap = (anim) => {
    if (rafHandle1Ref.current)  { cancelAnimationFrame(rafHandle1Ref.current);  rafHandle1Ref.current  = null; }
    if (rafHandle2Ref.current)  { cancelAnimationFrame(rafHandle2Ref.current);  rafHandle2Ref.current  = null; }
    if (snapTimerRef.current)   { clearTimeout(snapTimerRef.current);            snapTimerRef.current   = null; }
    if (completeHandler.current && anim) {
      try { anim.removeEventListener('complete', completeHandler.current); } catch (e) {}
      completeHandler.current = null;
    }
  };

  /**
   * After the animation segment finishes, snap to the resting frame for `targetIsOn`.
   *
   * Strategy: listen for lottie's own `complete` event. Lottie stops its internal RAF loop
   * before firing `complete`, so a synchronous `goToAndStop` call inside the handler runs
   * before the next browser paint — no rAF delay required. A fallback setTimeout fires in
   * case `complete` never fires (e.g. if the animation is destroyed mid-play).
   */
  const snapAfterAnimation = (anim, targetIsOn, fallbackMs) => {
    cancelPendingSnap(anim);

    const doSnap = () => {
      cancelPendingSnap(anim);
      animatingRef.current = false;
      try { anim.setSpeed(1); anim.setDirection(1); } catch (e) {}
      snapToSettledFrame(anim, targetIsOn);
    };

    // Primary path: complete fires when lottie has stopped its own RAF loop.
    // Call goToAndStop SYNCHRONOUSLY here — this updates lottie's currentFrame and
    // triggers a synchronous renderer.renderFrame() before the next browser paint.
    // No rAF or setTimeout delay needed or wanted.
    completeHandler.current = () => {
      doSnap();
    };
    try { anim.addEventListener('complete', completeHandler.current); } catch (e) {}

    // Fallback: if complete never fires, snap after the expected duration
    snapTimerRef.current = setTimeout(doSnap, fallbackMs);
  };

  // Initial load – create the Lottie instance once
  useEffect(() => {
    let isMounted = true;
    loadLottie()
      .then((lottie) => {
        if (!isMounted || !containerRef.current) return;

        const params = {
          container: containerRef.current,
          renderer: 'svg',
          loop: false,
          autoplay: false,
          rendererSettings: { preserveAspectRatio: 'xMidYMid meet' },
        };
        if (animationData && typeof animationData === 'object') params.animationData = animationData;
        else if (typeof src === 'string') params.path = src;
        else throw new Error('LottieToggle: missing animationData or src');

        animationRef.current = lottie.loadAnimation(params);

        const setupInitialFrame = () => {
          const anim = animationRef.current;
          if (!anim) return;
          setLoaded(true);
          // isOn is captured from props at mount time; if it's stale by the time this fires,
          // the isOn sync useEffect will correct it immediately after loaded becomes true.
          snapToSettledFrame(anim, isOn);
        };

        if (animationRef.current.animationData) {
          setupInitialFrame();
        } else {
          const t = setTimeout(setupInitialFrame, 50);
          const onDomLoaded = () => {
            setupInitialFrame();
            try { animationRef.current.removeEventListener('DOMLoaded', onDomLoaded); } catch (e) {}
            clearTimeout(t);
          };
          try { animationRef.current.addEventListener('DOMLoaded', onDomLoaded); } catch (e) {}
        }

        // If the user clicked before the animation was ready, play the queued segment now
        if (queuedTargetRef.current !== null) {
          const targetSeg = queuedTargetRef.current;
          queuedTargetRef.current = null;
          setTimeout(() => {
            if (!animatingRef.current && animationRef.current) {
              animatingRef.current = true;
              const anim = animationRef.current;
              const dur = targetSeg.durationMs || transitionMs;
              playSegmentRange(targetSeg.from, targetSeg.to, dur);
              snapAfterAnimation(anim, targetSeg.targetIsOn, dur + 400);
            }
          }, 20);
        }
      })
      .catch((err) => {
        console.error('LottieToggle failed to load lottie', err);
      });

    return () => {
      isMounted = false;
      cancelPendingSnap(animationRef.current);
      if (animationRef.current) {
        try { animationRef.current.destroy(); } catch (e) {}
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount-only

  // Sync to the resting frame when the parent changes `isOn` programmatically.
  // Guarded by lastSnappedRef so a server response that re-delivers the same useDarkMode
  // value (as a new userData object reference) doesn't trigger a spurious re-snap.
  useEffect(() => {
    const anim = animationRef.current;
    if (!anim || !loaded) return;
    if (animatingRef.current) return;
    if (lastSnappedRef.current === isOn) return; // already showing this state — skip
    snapToSettledFrame(anim, isOn);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOn, loaded]);

  const handleClick = (e) => {
    e.stopPropagation();
    const targetIsOn = !isOn;
    const chosenSeg = targetIsOn ? onSegment : offSegment;
    const anim = animationRef.current;

    if (!anim || !loaded || !anim.animationData) {
      queuedTargetRef.current = { ...chosenSeg, durationMs: transitionMs, targetIsOn };
    } else {
      try {
        animatingRef.current = true;
        playSegmentRange(chosenSeg.from, chosenSeg.to, transitionMs);
        // Snap via complete + double-rAF so we always execute after lottie's last SVG draw.
        // A fallback timeout handles edge-cases where complete never fires.
        snapAfterAnimation(anim, targetIsOn, transitionMs + 400);
      } catch (e) {
        console.warn('LottieToggle click play error', e);
        animatingRef.current = false;
      }
    }

    if (typeof toggleFunc === 'function') toggleFunc();
  };

  return (
    <div style={{ width, height, display: 'inline-block', cursor: 'pointer' }} onClick={handleClick}>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} aria-label="lottie-toggle" />
    </div>
  );
};

export default LottieToggle;
